import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogRef } from '@ngneat/dialog';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Indicator } from '../../../models/models';
import { AiService, SkillSuggestion } from '../../../services/ai.service';
import { EvaluationService } from '../../../services/evaluation.service';
import { IndicatorService } from '../../../services/indicator.service';
import { InvitationService } from '../../../services/invitation.service';
import { UserService } from '../../../services/user.service';
import { TreeSelectComponent } from '../../main/pages/evaluation-management/tree-select/tree-select.component';

interface EmailContact {
  email: string;
  userId?: string;
  name?: string;
  isRegistered: boolean;
}

@Component({
  selector: 'app-anonymous-feedback-modal',
  templateUrl: './anonymous-feedback-modal.component.html',
  styleUrls: ['./anonymous-feedback-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NgxTolgeeModule, TreeSelectComponent]
})
export class AnonymousFeedbackModalComponent implements OnInit {
  // Anonymous toggle (default: false = named feedback)
  isAnonymous = false;
  
  // Step 1: Title & Description
  evaluationTitle = '';
  evaluationDescription = '';
  
  // Step 2: AI-suggested skills
  loading = false;
  loadingSuggestions = false;
  allIndicators: Indicator[] = []; // Full indicator tree
  selectedIndicatorIds: string[] = []; // Selected indicator IDs for tree component
  skillsLocked = true; // Toggle for allowing recipients to modify skills
  
  // Step 3: Distribution method
  distributionMethod: 'email' | 'link' = 'email'; // Toggle between email and link
  emailContacts: EmailContact[] = [];
  emailInputValue = ''; // For the single email input field
  
  // Link options
  generatedLink = '';
  isOneTimeUse = false;
  
  // Other settings
  expireDays = 30;
  personalMessage = '';

  constructor(
    private dialogRef: DialogRef,
    private invitationService: InvitationService,
    private indicatorService: IndicatorService,
    private evaluationService: EvaluationService,
    private userService: UserService,
    private toast: HotToastService,
    private aiService: AiService
  ) {}

  ngOnInit(): void {
    this.loadIndicators();
  }

  loadIndicators(): void {
    this.loading = true;
    this.indicatorService.getFullIndicators().subscribe({
      next: (indicators: Indicator[]) => {
        this.allIndicators = indicators;
        this.loading = false;
        
        // Don't auto-select any indicators - user should use AI suggestions or select manually
        this.selectedIndicatorIds = [];
      },
      error: (err: any) => {
        console.error('Failed to load indicators:', err);
        this.toast.error('Failed to load skills');
        this.loading = false;
      }
    });
  }

  onIndicatorSelectionChange(selectedIds: string[]): void {
    this.selectedIndicatorIds = selectedIds;
  }

  /**
   * AI suggestion: Automatically suggests relevant skills based on title and description using Gemini AI
   */
  getSuggestedSkills(): void {
    if (!this.evaluationTitle.trim()) {
      return; // Silently skip if no title
    }

    // Check if description meets minimum length requirement (20 characters)
    const description = this.evaluationDescription?.trim() || '';
    if (description.length > 0 && description.length < 20) {
      return; // Skip if description is too short
    }

    this.loadingSuggestions = true;
    
    // Call the AI service
    this.aiService.suggestSkills({
      title: this.evaluationTitle,
      description: description || undefined
    }).subscribe({
      next: (suggestions: SkillSuggestion[]) => {
        if (suggestions && suggestions.length > 0) {
          // Extract indicator IDs from AI suggestions
          this.selectedIndicatorIds = suggestions.map(s => s.indicatorId);
          this.toast.success(`AI found ${suggestions.length} relevant skills based on your input!`, { duration: 2000 });
        } else {
          // Fallback to simple keyword matching if AI returns no results
          this.fallbackKeywordMatching();
        }
        this.loadingSuggestions = false;
      },
      error: (error) => {
        console.error('Error getting AI skill suggestions:', error);
        // Fallback to simple keyword matching on error
        this.fallbackKeywordMatching();
        this.loadingSuggestions = false;
      }
    });
  }

  /**
   * Fallback method using simple keyword matching if AI is unavailable
   */
  private fallbackKeywordMatching(): void {
    const searchText = (this.evaluationTitle + ' ' + this.evaluationDescription).toLowerCase();
    const keywords = searchText.split(/\s+/).filter(w => w.length > 2);
    
    const leafIndicators = this.allIndicators.filter(ind => 
      !this.allIndicators.some(other => other.masterId === ind.id)
    );
    
    const scoredIndicators = leafIndicators.map(ind => {
      let score = 0;
      const indText = (ind.name || '').toLowerCase();
      
      keywords.forEach(keyword => {
        if (indText.includes(keyword)) {
          score += 3;
        }
        
        const indWords = indText.split(/\s+/);
        indWords.forEach((word: string) => {
          if (word.startsWith(keyword.substring(0, Math.min(3, keyword.length)))) {
            score += 1;
          }
        });
      });
      
      return { indicator: ind, score };
    });
    
    const topSuggestions = scoredIndicators
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map(s => s.indicator);
    
    if (topSuggestions.length > 0) {
      this.selectedIndicatorIds = topSuggestions.map(ind => ind.id!).filter(Boolean);
      this.toast.success(`Found ${topSuggestions.length} relevant skills based on your title!`, { duration: 2000 });
    } else {
      this.selectedIndicatorIds = leafIndicators.slice(0, 8).map(ind => ind.id!).filter(Boolean);
      this.toast.info(`Showing ${this.selectedIndicatorIds.length} popular skills. Adjust your selection as needed.`, { duration: 2000 });
    }
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Handle email input (paste or enter)
   * Supports pasting multiple comma/semicolon/space-separated emails
   */
  onEmailInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';' || event.key === ' ') {
      event.preventDefault();
      this.addEmailFromInput();
    }
  }

  onEmailInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    
    // Split by common separators
    const emails = pastedText
      .split(/[,;\s\n\t]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
    
    emails.forEach(email => {
      if (this.isValidEmail(email) && !this.emailContacts.find(c => c.email === email)) {
        this.lookupAndAddEmail(email);
      }
    });
    
    this.emailInputValue = '';
  }

  addEmailFromInput(): void {
    const email = this.emailInputValue.trim();
    
    if (!email) return;
    
    if (!this.isValidEmail(email)) {
      this.toast.error('Invalid email address');
      return;
    }
    
    if (this.emailContacts.find(c => c.email === email)) {
      this.toast.warning('Email already added');
      this.emailInputValue = '';
      return;
    }
    
    this.lookupAndAddEmail(email);
    this.emailInputValue = '';
  }

  /**
   * Look up if email belongs to a registered user and add to contacts
   */
  private lookupAndAddEmail(email: string): void {
    const contact: EmailContact = {
      email,
      isRegistered: false
    };
    
    // Add immediately (optimistic)
    this.emailContacts.push(contact);
    
    // Look up user in background
    this.evaluationService.searchUsers(email).subscribe({
      next: (users: any[]) => {
        const user = users.find((u: any) => u.email?.toLowerCase() === email.toLowerCase());
        if (user) {
          contact.isRegistered = true;
          contact.name = `${user.firstName} ${user.lastName}`.trim();
          contact.userId = user.id;
        }
      },
      error: () => {
        // Keep as non-registered
      }
    });
  }

  removeEmailContact(index: number): void {
    this.emailContacts.splice(index, 1);
  }

  getValidEmailContacts(): EmailContact[] {
    return this.emailContacts.filter(c => c.email.trim() && this.isValidEmail(c.email.trim()));
  }

  /**
   * Generate a shareable link for the evaluation
   */
  generateLink(): void {
    if (!this.canGenerateLink()) {
      return;
    }

    this.loading = true;

    // Step 1: Get current user first
    this.userService.getUser().pipe(
      switchMap((currentUser: any) => {
        if (!currentUser) {
          throw new Error('You must be logged in to request feedback');
        }

        // Step 2: Prepare indicator IDs for metadata
        // If skills are not locked and none selected, use all leaf indicators
        let indicatorIds = this.selectedIndicatorIds;
        if (!this.skillsLocked && indicatorIds.length === 0) {
          const leafIndicators = this.allIndicators.filter(ind => 
            !this.allIndicators.some(other => other.masterId === ind.id)
          );
          indicatorIds = leafIndicators.map(ind => ind.id!).filter(Boolean);
        }
        
        // Step 3: Create invitation WITHOUT creating evaluation yet
        // Evaluation will be created on first submission
        return this.invitationService.createInvitation({
          type: 'EVALUATION_ANONYMOUS' as any,
          deliveryMethod: 'LINK' as any,
          evaluationId: undefined, // NO evaluation ID - will be created on first submission
          relatedUserId: currentUser.id, // User who will receive feedback
          expirationDays: this.expireDays,
          personalMessage: this.personalMessage || undefined,
          inviteeEmail: this.isOneTimeUse ? `one-time-${Date.now()}@link.local` : undefined,
          metadata: {
            evaluationTitle: this.evaluationTitle, // Store title for later evaluation creation
            skillsLocked: this.skillsLocked,
            allowedIndicatorIds: indicatorIds.length > 0 ? indicatorIds : undefined,
            message: this.personalMessage || undefined,
            isAnonymous: this.isAnonymous // NEW: Track if feedback should be anonymous
          } as any
        });
      })
    ).subscribe({
      next: (result: any) => {
        this.generatedLink = this.invitationService.generateInvitationUrl(result.invitation);
        this.toast.success('Link generated successfully!');
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Failed to generate link:', err);
        this.toast.error('Failed to generate link. Please try again.');
        this.loading = false;
      }
    });
  }

  copyLinkToClipboard(): void {
    navigator.clipboard.writeText(this.generatedLink).then(() => {
      this.toast.success('Link copied to clipboard!');
    });
  }

  canGenerateLink(): boolean {
    return this.evaluationTitle.trim().length > 0 &&
           (!this.skillsLocked || this.selectedIndicatorIds.length > 0) &&
           !this.loading;
  }

  canSend(): boolean {
    return this.evaluationTitle.trim().length > 0 &&
           (!this.skillsLocked || this.selectedIndicatorIds.length > 0) &&
           this.getValidEmailContacts().length > 0 && 
           !this.loading;
  }

  sendInvitations(): void {
    if (!this.canSend()) {
      return;
    }

    const validContacts = this.getValidEmailContacts();
    this.loading = true;

    // Step 1: Get current user first
    this.userService.getUser().pipe(
      switchMap((currentUser: any) => {
        if (!currentUser) {
          throw new Error('You must be logged in to request feedback');
        }

        // Step 2: Prepare indicator IDs for metadata
        // If skills are not locked and none selected, use all leaf indicators
        let indicatorIds = this.selectedIndicatorIds;
        if (!this.skillsLocked && indicatorIds.length === 0) {
          const leafIndicators = this.allIndicators.filter(ind => 
            !this.allIndicators.some(other => other.masterId === ind.id)
          );
          indicatorIds = leafIndicators.map(ind => ind.id!).filter(Boolean);
        }
        
        // Step 3: Create invitations WITHOUT creating evaluation yet
        // Each invitation will store metadata for evaluation creation on first submission
        const invitations$ = validContacts.map(contact =>
          this.invitationService.createInvitation({
            type: 'EVALUATION_ANONYMOUS' as any,
            deliveryMethod: 'EMAIL' as any,
            evaluationId: undefined, // NO evaluation ID - will be created on first submission
            relatedUserId: currentUser.id, // User who will receive feedback
            inviteeEmail: contact.email,
            expirationDays: this.expireDays,
            personalMessage: this.personalMessage || undefined,
            metadata: {
              evaluationTitle: this.evaluationTitle, // Store title for later evaluation creation
              evaluationDescription: this.evaluationDescription || undefined, // Store description for later evaluation creation
              skillsLocked: this.skillsLocked,
              allowedIndicatorIds: indicatorIds.length > 0 ? indicatorIds : undefined,
              message: this.personalMessage || undefined,
              isAnonymous: this.isAnonymous // NEW: Track if feedback should be anonymous
            } as any
          })
        );
        return forkJoin(invitations$);
      })
    ).subscribe({
      next: (results: any[]) => {
        const successCount = results.filter(r => r?.success !== false).length;
        const failCount = results.length - successCount;

        if (failCount === 0) {
          this.toast.success(`Successfully sent ${successCount} invitation${successCount > 1 ? 's' : ''}!`);
          this.dialogRef.close({ success: true, count: successCount });
        } else {
          this.toast.warning(`Sent ${successCount} invitation(s), ${failCount} failed`);
          this.dialogRef.close({ success: true, count: successCount, failed: failCount });
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Failed to create evaluation or send invitations:', err);
        this.toast.error('Failed to send invitations. Please try again.');
        this.loading = false;
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}

